import { AuthenticationResult, PublicClientApplication } from "@azure/msal-browser";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { useEffect, useState } from "react";

const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID;
const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID;
const API_SCOPES = [import.meta.env.VITE_ENTRA_API_SCOPE];

/**
 * Teams認証に必要なメソッドを持つインターフェース
 */
interface TeamsUserCredential {
  getUserInfo(): Promise<{ displayName: string; }>;
  getToken(scopes: string[]): Promise<{ token?: string; }>;
  login(scopes: string | string[], resources?: string[]): Promise<void>;
}

/**
 * 指定した時間待機する関数
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * トークン取得を最大3回リトライする関数
 */
const getTokenWithRetry = async (
  teamsUserCredential: TeamsUserCredential,
  scopes: string[],
  maxRetries: number = 3,
  delayMs: number = 300,
): Promise<{ token?: string; }> => {
  let retryCount = 0;
  let lastError: Error | null = null;

  while (retryCount < maxRetries) {
    try {
      if (retryCount > 0) {
        console.log(`Retry attempt ${retryCount} for token acquisition`);
        await sleep(delayMs);
      }

      const accessToken = await teamsUserCredential.getToken(scopes);
      return accessToken;
    } catch (e: unknown) {
      const error = e as { code?: string; message?: string; };
      lastError = e as Error;
      console.error(`Token acquisition failed (attempt ${retryCount + 1}/${maxRetries}):`, error);

      if (error.code === "UiRequiredError") {
        try {
          console.log("Attempting explicit login due to UiRequiredError");
          await teamsUserCredential.login(scopes);
          console.log("Login successful, continuing with retry");
        } catch (loginError) {
          console.error("Login attempt failed:", loginError);
        }
      }

      retryCount++;
    }
  }

  throw lastError || new Error("Failed to acquire token after multiple retries");
};

/**
 * Teams認証処理を行う関数
 */
const handleTeamsAuth = (
  teamsUserCredential: TeamsUserCredential,
  setUserName: (name: string) => void,
  setToken: (token: string) => void,
  setAuthCompleted: (completed: boolean) => void,
) => {
  teamsUserCredential.getUserInfo().then((userInfo: { displayName: string; }) => {
    setUserName(userInfo.displayName);
  }).catch((e: Error) => {
    console.error(e);
  });

  getTokenWithRetry(teamsUserCredential, API_SCOPES)
    .then((accessToken: { token?: string; }) => {
      setToken(accessToken?.token || "");
      setAuthCompleted(true);
    })
    .catch((e: Error) => {
      console.error("All token acquisition attempts failed:", e);
    });
};

/**
 * Web認証処理を行う関数
 */
const handleWebAuth = (
  setUserName: (name: string) => void,
  setToken: (token: string) => void,
  setAuthCompleted: (completed: boolean) => void,
) => {
  // MSALの設定
  const msalConfig = {
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
  };

  // MSALインスタンスの初期化
  const msalInstance = new PublicClientApplication(msalConfig);

  // MSALインスタンスを初期化してから使用する
  msalInstance.initialize().then(() => {
    // リダイレクト処理のハンドリング
    msalInstance.handleRedirectPromise()
      .then((response: AuthenticationResult | null) => {
        // リダイレクト後のレスポンス処理
        if (response) {
          // ユーザー情報を取得
          const account = response.account;
          if (account) {
            setUserName(account.name || account.username || "");
          }

          // トークンを設定
          if (response.accessToken) {
            setToken(response.accessToken);
            setAuthCompleted(true);
          }
        } else {
          // アクティブなアカウントがあるか確認
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // 既存のアカウントがある場合
            const account = accounts[0];
            setUserName(account.name || account.username || "");
            // サイレント認証を試みる
            msalInstance.acquireTokenSilent({
              scopes: API_SCOPES,
              account: account,
            }).then((tokenResponse: { accessToken: string; }) => {
              setToken(tokenResponse.accessToken);
              setAuthCompleted(true);
            }).catch((error: Error) => {
              console.error("Silent token acquisition failed", error);
              // サイレント認証に失敗した場合はリダイレクト認証を実行
              msalInstance.acquireTokenRedirect({
                scopes: API_SCOPES,
              });
            });
          } else {
            // アカウントがない場合はログインリダイレクトを実行
            msalInstance.loginRedirect({
              scopes: API_SCOPES,
              redirectUri: window.location.origin,
            });
          }
        }
      });
  }).catch((error: Error) => {
    console.error("MSAL initialization failed", error);
  });
};

const useAuth = () => {
  const [userName, setUserName] = useState("");
  const [token, setToken] = useState("");
  const [authCompleted, setAuthCompleted] = useState(false);

  const { themeString, teamsUserCredential, inTeams } = useTeamsUserCredential({
    // [Note] iframeにて認証するため、Originの一致の確認のためにルートに配置する必要がある
    initiateLoginEndpoint: window.location.origin + "/auth-start.html",
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
  });

  useEffect(() => {
    if (teamsUserCredential === undefined || inTeams === undefined) {
      // まだ初期化中（ロード中）なので何もしない
      console.log("initializing");
      return;
    }
    if (inTeams) {
      console.log("inTeams");
      handleTeamsAuth(teamsUserCredential, setUserName, setToken, setAuthCompleted);
    } else {
      console.log("not inTeams");
      handleWebAuth(setUserName, setToken, setAuthCompleted);
    }
  }, [teamsUserCredential, inTeams]);
  return { themeString, userName, token, authCompleted };
};

export default useAuth;
